import { findConflicts, movedSlot, type CancelKind, type SessionStatus, type TherapySession } from '@selfon/domain/browser';

/* 달력 공통 계산. 회기 시각은 모두 한국 시간(+09:00) 문자열이라 문자열로 날짜·분을 바로 읽는다. */

export type View = 'staff' | 'week' | 'month' | 'list';
/** 표시 상태 필터 키: 취소는 구분별로 따로 켜고 끈다 */
export type StatusKey = 'SCHEDULED' | 'COMPLETED' | 'NO_SHOW' | 'CANCELLED_PLAIN' | 'CANCELLED_MAKEUP' | 'CANCELLED_CARRYOVER';
export const STATUS_KEYS: StatusKey[] = ['SCHEDULED', 'COMPLETED', 'NO_SHOW', 'CANCELLED_PLAIN', 'CANCELLED_MAKEUP', 'CANCELLED_CARRYOVER'];
export const STATUS_KEY_LABEL: Record<StatusKey, string> = {
  SCHEDULED: '예정', COMPLETED: '완료', NO_SHOW: '결석', CANCELLED_PLAIN: '취소', CANCELLED_MAKEUP: '취소(보강)', CANCELLED_CARRYOVER: '취소(이월)',
};
export const CANCEL_LABEL: Record<CancelKind, string> = { PLAIN: '취소', MAKEUP: '취소(보강)', CARRYOVER: '취소(이월)' };
export const statusKey = (s: { status: SessionStatus; cancelKind?: CancelKind }): StatusKey =>
  s.status === 'CANCELLED' ? (`CANCELLED_${s.cancelKind ?? 'PLAIN'}` as StatusKey) : s.status;
export const statusText = (s: { status: SessionStatus; cancelKind?: CancelKind }) => STATUS_KEY_LABEL[statusKey(s)];

export const dateOf = (iso: string) => iso.slice(0, 10);
export const minOf = (iso: string) => Number(iso.slice(11, 13)) * 60 + Number(iso.slice(14, 16));
export const hm = (iso: string) => iso.slice(11, 16);
export const pad = (n: number) => String(n).padStart(2, '0');
export const hhmm = (min: number) => `${pad(Math.floor(min / 60))}:${pad(min % 60)}`;

export function addDays(date: string, n: number) {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
export const weekday = (date: string) => new Date(`${date}T00:00:00Z`).getUTCDay(); // 0=일
export const mondayOf = (date: string) => addDays(date, -((weekday(date) + 6) % 7));
export const monthOf = (date: string) => date.slice(0, 7);
export function addMonths(date: string, n: number) {
  const [y, m] = date.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return d.toISOString().slice(0, 10);
}
export const WEEKDAY = ['일', '월', '화', '수', '목', '금', '토'];
export const dayLabel = (date: string) => `${Number(date.slice(8))}일 (${WEEKDAY[weekday(date)]})`;

/** 보기별 화면에 보이는 날짜들 */
export function visibleDays(view: View, cursor: string, showSunday: boolean): string[] {
  if (view === 'staff') return [cursor];
  if (view === 'week' || view === 'list') {
    const mon = mondayOf(cursor);
    return Array.from({ length: 7 }, (_, i) => addDays(mon, i)).filter((d) => showSunday || weekday(d) !== 0);
  }
  // 월: 그 달 1일이 포함된 주의 월요일부터 6주
  const first = `${monthOf(cursor)}-01`;
  const start = mondayOf(first);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i)).filter((d) => showSunday || weekday(d) !== 0);
}
export const monthsIn = (days: string[]) => [...new Set(days.map(monthOf))];

/** 선생님 색(색상환 각도). 라이트·다크 명도는 CSS 토큰이 정한다. */
const HUES = [212, 145, 28, 275, 340, 178, 48, 250, 0, 100, 195, 310];
export const hueOf = (index: number) => HUES[index % HUES.length]!;

/**
 * 같은 날 겹치는 일정을 나란히 놓기 위한 배치: 겹치는 묶음마다 칸(lane) 수를 정하고 각 일정에 칸 번호를 준다.
 */
export function layoutLanes<T extends { startAt: string; endAt: string }>(items: T[]): Array<{ item: T; lane: number; lanes: number }> {
  const sorted = [...items].sort((a, b) => minOf(a.startAt) - minOf(b.startAt) || minOf(b.endAt) - minOf(a.endAt));
  const out: Array<{ item: T; lane: number; lanes: number }> = [];
  let cluster: Array<{ item: T; lane: number; lanes: number }> = [];
  let clusterEnd = -1;
  const laneEnds: number[] = [];
  const flush = () => { const n = Math.max(1, ...cluster.map((c) => c.lane + 1)); for (const c of cluster) c.lanes = n; out.push(...cluster); cluster = []; laneEnds.length = 0; };
  for (const item of sorted) {
    const s = minOf(item.startAt), e = minOf(item.endAt);
    if (cluster.length && s >= clusterEnd) flush();
    let lane = laneEnds.findIndex((end) => end <= s);
    if (lane < 0) { lane = laneEnds.length; laneEnds.push(e); } else laneEnds[lane] = e;
    cluster.push({ item, lane, lanes: 1 });
    clusterEnd = Math.max(clusterEnd, e);
  }
  if (cluster.length) flush();
  return out;
}

export interface Filters {
  hiddenStaff: string[];
  statuses: StatusKey[];
  clientId: string;
  program: string;
  voucher: string; // '' 전체, '__none' 비바우처
}
export function applyFilters(sessions: TherapySession[], f: Filters) {
  const hidden = new Set(f.hiddenStaff);
  const statuses = new Set(f.statuses);
  return sessions.filter((s) => !hidden.has(s.therapistId) && statuses.has(statusKey(s))
    && (!f.clientId || s.clientId === f.clientId) && (!f.program || s.program === f.program)
    && (!f.voucher || (f.voucher === '__none' ? !s.voucherProgram : s.voucherProgram === f.voucher)));
}

export interface MoveTo { date: string; startTime: string; therapistId: string }
export const sameSpot = (s: TherapySession, to: MoveTo) => to.therapistId === s.therapistId && to.date === dateOf(s.startAt) && to.startTime === hm(s.startAt);

/**
 * 끌어 놓기 전에 화면에서 미리 보는 검사(최종 판단은 서버). 문제가 없으면 null.
 * 겹침은 화면에 불러온 달의 회기만으로 계산한다 — 필터로 숨긴 선생님 일정도 포함해서.
 */
export function moveProblem(s: TherapySession, to: MoveTo, all: readonly TherapySession[], me: { role: string; userId: string }, nowIso: string): string | null {
  if (me.role === 'THERAPIST' && to.therapistId !== me.userId) return '다른 선생님 시간표로는 옮길 수 없습니다.';
  let slot: { startAt: string; endAt: string };
  try { slot = movedSlot(s, to); } catch (e) { return e instanceof Error ? e.message : '옮길 수 없는 시간입니다.'; }
  if (sameSpot(s, to)) return null;
  if (Date.parse(slot.startAt) < Date.parse(nowIso)) return '지난 시각으로는 옮길 수 없습니다.';
  const c = findConflicts(s, { ...slot, therapistId: to.therapistId }, all)[0];
  if (c) return c.reason === 'THERAPIST_BUSY' ? `선생님의 다른 수업(${hm(c.startAt)})과 겹칩니다.` : `이 아동의 다른 수업(${hm(c.startAt)})과 겹칩니다.`;
  return null;
}

/** 화면 설정은 이 브라우저에만 기억(저장 불가 환경이면 기본값) */
export function loadPref<T>(key: string, fallback: T): T {
  try { const v = localStorage.getItem(key); return v ? { ...fallback, ...JSON.parse(v) } : fallback; } catch { return fallback; }
}
export function savePref(key: string, v: unknown) { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* 무시 */ } }
